import { ReplyAction, SentMessageId, UserMailboxMessageId } from "@sp-crm/core";

export interface ReplyState {
    initialRecipients: string[];
    initialCc: string[];
    initialSubject: string;
    initialBody?: string;
    inReplyToUserMailboxMessageId?: UserMailboxMessageId;
    inReplyToSentMessageId?: SentMessageId;
    replyAction?: ReplyAction;
}

export interface SentMessageForReply {
    id: string;
    from?: string | null;
    to?: string[] | null;
    cc?: string[] | null;
}

export interface ReceivedMessageContact {
    email?: string | null;
}

export interface ReceivedMessageRecipient {
    contact: ReceivedMessageContact;
}

export interface ReceivedMessageForReply {
    id: string;
    from?: { email?: string | null } | null;
    replyTo?: ReceivedMessageRecipient[] | null;
    to: ReceivedMessageRecipient[];
    cc: ReceivedMessageRecipient[];
}

export function buildReplySubject(subject: string): string {
    if (/^re:/i.test(subject)) {
        return subject;
    }
    return `Re: ${subject}`;
}

export function isCurrentUserEmail(email: string, currentUserEmails: string[]): boolean {
    const lower = email.toLowerCase();
    return currentUserEmails.some(e => e.toLowerCase() === lower);
}

export function buildReplyFromSent(
    sent: SentMessageForReply,
    threadSubject: string,
    replyType: ReplyAction,
    currentUserEmails: string[],
): ReplyState {
    const senderEmail = sent.from;
    const isCurrentUserSender =
        senderEmail && isCurrentUserEmail(senderEmail, currentUserEmails);

    if (replyType === ReplyAction.Reply) {
        const replyTo = isCurrentUserSender
            ? (sent.to ?? [])
            : senderEmail
              ? [senderEmail]
              : [];
        return {
            initialRecipients: replyTo,
            initialCc: [],
            initialSubject: buildReplySubject(threadSubject),
            inReplyToSentMessageId: sent.id as SentMessageId,
            replyAction: replyType,
        };
    }

    const seen = new Set<string>();
    const toRecipients = [
        ...(senderEmail ? [senderEmail] : []),
        ...(sent.to ?? []),
    ].filter(e => {
        const lower = e.toLowerCase();
        if (seen.has(lower)) return false;
        seen.add(lower);
        if (isCurrentUserEmail(e, currentUserEmails)) return false;
        return true;
    });

    const ccRecipients = (sent.cc ?? []).filter(e => {
        const lower = e.toLowerCase();
        if (seen.has(lower)) return false;
        seen.add(lower);
        if (isCurrentUserEmail(e, currentUserEmails)) return false;
        return true;
    });

    return {
        initialRecipients: toRecipients,
        initialCc: ccRecipients,
        initialSubject: buildReplySubject(threadSubject),
        inReplyToSentMessageId: sent.id as SentMessageId,
        replyAction: replyType,
    };
}

export function buildReplyFromReceived(
    received: ReceivedMessageForReply,
    threadSubject: string,
    replyType: ReplyAction,
    currentUserEmails: string[],
): ReplyState {
    const fromEmail = received.from?.email;
    const replyToEmails = (received.replyTo ?? [])
        .map(r => r.contact.email)
        .filter((e): e is string => !!e);
    const primaryRecipients =
        replyToEmails.length > 0 ? replyToEmails : fromEmail ? [fromEmail] : [];
    let initialCc: string[] = [];
    if (replyType === ReplyAction.ReplyAll) {
        const toEmails = received.to
            .map(r => r.contact.email)
            .filter((e): e is string => !!e);
        const ccEmails = received.cc
            .map(r => r.contact.email)
            .filter((e): e is string => !!e);
        const excluded = [...primaryRecipients, ...(fromEmail ? [fromEmail] : [])].map(
            e => e.toLowerCase(),
        );
        const seen = new Set<string>();
        initialCc = [...toEmails, ...ccEmails].filter(e => {
            const lower = e.toLowerCase();
            if (seen.has(lower)) return false;
            seen.add(lower);
            if (excluded.includes(lower)) return false;
            if (isCurrentUserEmail(e, currentUserEmails)) return false;
            return true;
        });
    }
    return {
        initialRecipients: primaryRecipients,
        initialCc,
        initialSubject: buildReplySubject(threadSubject),
        inReplyToUserMailboxMessageId: received.id as UserMailboxMessageId,
        replyAction: replyType,
    };
}
